/**
 * useChartGeneration - Hook for generating clinical notes from transcripts
 *
 * Takes a transcript, selected diagnoses, and template to generate
 * a structured clinical note using the AI backend via proxyRunWorkflow.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  getDefaultTemplate,
  parseNoteResponse,
  titleFromSectionKey,
} from "config/noteTemplates";
import { invokeLLMPrompt } from "services/llmService";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import { useAuth } from "hooks/useAuth";

const NOTES_WORKFLOW_NAME = "chartmind-notes";

/** Build a Chart template from the keys a note response actually returned. */
const templateFromSections = (name, sectionsMap, preferredOrder = []) => {
  const keys = Object.keys(sectionsMap || {});
  const ordered = [
    ...preferredOrder.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !preferredOrder.includes(key)),
  ];
  return {
    name: name || "Clinical note",
    sections: ordered.map((key) => ({
      key,
      title: titleFromSectionKey(key),
    })),
  };
};

/**
 * Format selected diagnoses for inclusion in the LLM prompt.
 * namesOnly (Events note): condition names as bullets — no likelihood, urgency, or rationale.
 */
const formatDiagnosesForPrompt = (diagnoses, { namesOnly = false } = {}) => {
  if (!diagnoses || diagnoses.length === 0) return "";

  if (namesOnly) {
    const lines = diagnoses
      .map((dx) => String(dx.condition || dx.name || "").trim())
      .filter(Boolean)
      .map((name) => `- ${name}`);
    if (!lines.length) return "";
    return `\n\nSELECTED DIAGNOSES (confirmed by provider):\n${lines.join("\n")}`;
  }

  const formatted = diagnoses
    .map((dx, i) => {
      let line = `${i + 1}. ${dx.condition}`;
      if (dx.likelihood) line += ` (${dx.likelihood})`;
      if (dx.urgent) line += " [URGENT]";
      if (dx.rationale) line += `\n   Rationale: ${dx.rationale}`;
      return line;
    })
    .join("\n");

  return `\n\nSELECTED DIAGNOSES (confirmed by provider):\n${formatted}`;
};

/**
 * Tests and treatments the clinician chose, for callers that collect them before the note.
 *
 * ChartMind passes none, so its query is unchanged. ChartMind V2 passes both: without them its
 * note was written from the transcript and diagnoses alone, and every result entered on the Tests
 * step and every treatment ticked was silently left out.
 *
 * @param {Object} details
 * @param {Array<{name: string, result?: string}>} details.tests - Tests on the plan; `result`
 *   is the entered result as text, absent when none was entered.
 * @param {Array<{name: string, category?: string, route?: string}>} details.treatments
 * @param {string} [details.inpatient] - Daily Plan, Pending and Events, already formatted (see
 *   buildInpatientNoteContext). Outpatient encounters omit it.
 */
const formatEncounterDetailsForPrompt = ({
  tests = [],
  treatments = [],
  inpatient = "",
  examDiagnostics = "",
} = {}) => {
  let text = "";
  // Prefer structured Exam / Diagnostics *2 blocks when present (Admission/Progress/outpatient).
  if (String(examDiagnostics || "").trim()) {
    text += `\n\n${String(examDiagnostics).trim()}`;
  } else if (tests.length) {
    const lines = tests
      .map((t) => `- ${t.name}: ${t.result ? `Result: ${t.result}` : "Ordered, no result entered"}`)
      .join("\n");
    text += `\n\nDIAGNOSTIC TESTS (chosen by provider):\n${lines}`;
  }
  if (treatments.length) {
    const lines = treatments
      .map((t) => {
        const extra = [t.category, t.route].filter(Boolean).join(", ");
        return `- ${t.name}${extra ? ` (${extra})` : ""}`;
      })
      .join("\n");
    text += `\n\nTREATMENT PLAN (chosen by provider):\n${lines}`;
  }
  if (inpatient.trim()) {
    text += `\n\n${inpatient.trim()}`;
  }
  return text;
};

/**
 * useChartGeneration Hook
 *
 * @param {Object} options
 * @param {Object} options.template - Note template (defaults to SOAP)
 * @param {Object} options.userSettings - Provider settings { specialty, displayName, credentials }
 * @param {Function} options.onDataChanged - Callback fired after successful AI response (for auto-save)
 * @param {string} [options.promptId] - Prompt to generate with (inpatient passes its own).
 *   Clinical references stay on the base prompt, which is where they are tagged.
 * @param {string} [options.fallbackPromptId] - Used if `promptId` is not set up on this environment
 * @param {string} [options.fallbackInstructions] - Appended to the request only when it falls back,
 *   to carry instructions the missing prompt would otherwise have held
 * @param {string} [options.documentationOutline] - ChartMind's specialty outline, sent after the
 *   template line (see formatDocumentationOutline)
 * @param {string} [options.focus] - Specialty and setting focus, appended to the request
 * @param {Function} [options.finalizeSections] - Adjusts the parsed note before it is shown
 *   (inpatient adds back any pending item the model dropped)
 * @param {boolean} [options.flexibleSections] - Keep every key the model returns and drive the
 *   Chart display from that object (Events note / QueryManager-owned section list)
 * @param {boolean} [options.diagnosesNamesOnly] - Events note: send diagnosis names only (no
 *   likelihood, urgency, or rationale) so Assessment does not echo ranking commentary
 */
function useChartGeneration({
  template,
  userSettings,
  onDataChanged,
  promptId = NOTES_WORKFLOW_NAME,
  fallbackPromptId,
  fallbackInstructions,
  documentationOutline,
  focus,
  finalizeSections,
  flexibleSections = false,
  diagnosesNamesOnly = false,
} = {}) {
  const { organizationId } = useAuth();
  const [noteSections, setNoteSections] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [clinicalReferences, setClinicalReferences] = useState([]);
  const [embeddedDocuments, setEmbeddedDocuments] = useState([]);
  const [resolvedTemplate, setResolvedTemplate] = useState(null);

  // Use provided template or default SOAP
  const baseTemplate = template || getDefaultTemplate();
  const activeTemplate = useMemo(() => {
    if (flexibleSections && resolvedTemplate) return resolvedTemplate;
    return baseTemplate;
  }, [flexibleSections, resolvedTemplate, baseTemplate]);

  // Track whether the last noteSections change came from a user edit (updateSection)
  // vs. AI generation (generateNote already calls onDataChanged synchronously).
  const pendingUserEditRef = useRef(false);

  // After updateSection's setNoteSections flushes, fire onDataChanged with fresh state.
  useEffect(() => {
    if (pendingUserEditRef.current && noteSections && onDataChanged) {
      pendingUserEditRef.current = false;
      onDataChanged();
    }
  }, [noteSections, onDataChanged]);

  const upsertFeedback = useCallback((updates) => {
    setFeedback((prev) => {
      const next = {
        ...(prev || {}),
        ...updates,
      };

      const hasRating =
        typeof next.rating === "number" && Number.isFinite(next.rating) && next.rating > 0;
      const hasRemarks =
        typeof next.remarks === "string" && next.remarks.trim().length > 0;

      if (!hasRating && !hasRemarks) {
        return null;
      }

      return {
        ...(hasRating ? { rating: next.rating } : {}),
        ...(hasRemarks ? { remarks: next.remarks } : {}),
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  /**
   * Generate clinical note from transcript and diagnoses
   *
   * @param {string} transcript - The encounter transcript
   * @param {Array} selectedDiagnoses - Array of selected diagnosis objects
   * @param {Object|null} patientContext - Payload from buildPatientContextPayload
   * @param {Object} [encounterDetails] - Optional tests and treatments, see
   *   formatEncounterDetailsForPrompt
   */
  const generateNote = useCallback(
    async (transcript, selectedDiagnoses = [], patientContext = null, encounterDetails = null) => {
      if (!transcript || transcript.trim().length < 50) {
        setError("Transcript is too short. Need at least 50 characters.");
        return null;
      }

      setLoading(true);
      setError(null);
      setRawResponse(null);

      try {
        // Build the query with all context for the backend workflow
        const diagnosisInfo = formatDiagnosesForPrompt(selectedDiagnoses, {
          namesOnly: diagnosesNamesOnly,
        });
        const preferredSections = baseTemplate.sections || [];
        const templateInfo = flexibleSections
          ? `NOTE TEMPLATE: ${baseTemplate.name}`
          : `NOTE TEMPLATE: ${baseTemplate.name}\nSECTIONS: ${preferredSections.map((s) => s.key).join(", ")}`;
        const detailsInfo = encounterDetails ? formatEncounterDetailsForPrompt(encounterDetails) : "";
        const outlineInfo = documentationOutline ? `\n\n${documentationOutline}` : "";
        const query = `${templateInfo}${outlineInfo}\n\nPATIENT ENCOUNTER TRANSCRIPT:\n\n${transcript}${diagnosisInfo}${detailsInfo}`;

        console.log("[useChartGeneration] 📝 GENERATING NOTE VIA WORKFLOW...", {
          workflowName: promptId,
          templateName: baseTemplate.name,
          sectionsCount: preferredSections.length,
          transcriptLength: transcript.length,
          diagnosesCount: selectedDiagnoses.length,
          timestamp: new Date().toISOString(),
        });
        const invokeNote = () =>
          invokeLLMPrompt({
            promptId,
            fallbackPromptId,
            focus,
            ...(fallbackInstructions ? { fallbackUserMessage: `${query}\n\n${fallbackInstructions}` } : {}),
            userMessage: query,
            organizationId,
            patientContext,
            includeEmbeddedDocuments: true,
            embeddedDocumentsOptions: { chunkLimit: 8 },
          });

        // Call workflow API
        const [firstResult, refs] = await Promise.all([
          invokeNote(),
          retrieveClinicalReferences(transcript, {
            promptId: NOTES_WORKFLOW_NAME,
          }).catch((err) => {
            console.warn("[useChartGeneration] Clinical references retrieval failed:", err);
            return [];
          }),
        ]);

        let result = firstResult;
        setClinicalReferences(refs);
        setEmbeddedDocuments(result.embeddedDocuments?.documents ?? []);

        // Parse the response. When the org supplied custom prompt rules, honor
        // an "omit empty sections" intent by blanking out sections the model
        // filled with "not documented" text against instructions.
        const parseResult = (res) => {
          setRawResponse(res.output);
          console.log("[useChartGeneration] Response received", {
            responseLength: res.output?.length || 0,
            preview: res.output?.substring(0, 200),
          });
          return parseNoteResponse(res.output, preferredSections, {
            omitEmptySections: Boolean(res.meta?.organizationRulesApplied),
            includeExtraKeys: flexibleSections,
          });
        };

        let parsed = parseResult(result);
        // A malformed response is a one-off from the model, not a problem with the encounter,
        // so ask once more before showing the clinician an error.
        if (!parsed) {
          console.warn("[useChartGeneration] Unparseable note, retrying once");
          result = await invokeNote();
          setEmbeddedDocuments(result.embeddedDocuments?.documents ?? []);
          parsed = parseResult(result);
        }

        if (parsed && finalizeSections) parsed = finalizeSections(parsed);

        if (parsed) {
          if (flexibleSections) {
            setResolvedTemplate(
              templateFromSections(
                baseTemplate.name,
                parsed,
                preferredSections.map((s) => s.key),
              ),
            );
          }
          setNoteSections(parsed);
          setFeedback(null);
          console.log("[useChartGeneration] Note generated successfully", {
            sections: Object.keys(parsed),
          });

          // Trigger auto-save after successful AI response
          if (onDataChanged) {
            console.log("[useChartGeneration] Triggering auto-save callback");
            onDataChanged();
          }

          return parsed;
        } else {
          setError("We couldn't write the note this time. Please try again.");
          return null;
        }
      } catch (err) {
        console.error("[useChartGeneration] Error:", err);
        setError(err.message || "Failed to generate clinical note");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [baseTemplate, userSettings, onDataChanged, organizationId, promptId, fallbackPromptId, fallbackInstructions, documentationOutline, focus, finalizeSections, flexibleSections, diagnosesNamesOnly],
  );

  /**
   * Update a specific section of the note
   */
  const updateSection = useCallback((sectionKey, value) => {
    pendingUserEditRef.current = true;
    setNoteSections((prev) => ({
      ...prev,
      [sectionKey]: value,
    }));
  }, []);

  /**
   * Store chart feedback for the current note draft
   */
  const updateFeedbackRating = useCallback((rating) => {
    upsertFeedback({
      rating:
        typeof rating === "number" && Number.isFinite(rating) && rating > 0
          ? rating
          : null,
    });
  }, [upsertFeedback]);

  /**
   * Store free-text remarks for the current note draft
   */
  const updateFeedbackRemarks = useCallback((remarks) => {
    upsertFeedback({
      remarks: typeof remarks === "string" ? remarks : "",
    });
  }, [upsertFeedback]);

  /**
   * Clear all note data
   */
  const clearNote = useCallback(() => {
    setNoteSections(null);
    setResolvedTemplate(null);
    setFeedback(null);
    setError(null);
    setRawResponse(null);
    setClinicalReferences([]);
    setEmbeddedDocuments([]);
  }, []);

  /**
   * Format note for clipboard (plain text)
   */
  const formatForClipboard = useCallback(() => {
    if (!noteSections) return "";

    let text = `${activeTemplate.name.toUpperCase()}\n${"=".repeat(
      activeTemplate.name.length + 5,
    )}\n\n`;

    activeTemplate.sections.forEach((section) => {
      const content = noteSections[section.key] || "";
      text += `${section.title.toUpperCase()}:\n${content}\n\n`;
    });

    text += `---\nGenerated by ChartMind\n${new Date().toLocaleString()}`;
    return text;
  }, [noteSections, activeTemplate]);

  /**
   * Directly set noteSections (for hydrating from saved session)
   */
  const setNoteSectionsDirectly = useCallback((sections) => {
    console.log(
      "[useChartGeneration] setNoteSectionsDirectly called with:",
      sections,
    );
    setNoteSections(sections);
  }, []);

  /**
   * Directly set feedback (for hydrating from saved session)
   */
  const setFeedbackDirectly = useCallback((nextFeedback) => {
    setFeedback(nextFeedback || null);
  }, []);

  /**
   * Restore source documents (for hydrating from saved session).
   * Resets to empty when the saved session has none.
   */
  const hydrateSourceDocuments = useCallback((data) => {
    setClinicalReferences(data?.clinicalReferences || []);
    setEmbeddedDocuments(data?.embeddedDocuments || []);
  }, []);

  return {
    // State
    noteSections,
    feedback,
    loading,
    error,
    rawResponse,
    clinicalReferences,
    embeddedDocuments,
    template: activeTemplate,

    // Computed
    hasNote: !!noteSections,

    // Actions
    generateNote,
    updateSection,
    updateFeedbackRating,
    updateFeedbackRemarks,
    clearNote,
    formatForClipboard,
    setNoteSections: setNoteSectionsDirectly, // For session hydration
    setFeedback: setFeedbackDirectly, // For session hydration
    hydrateSourceDocuments, // For session hydration
  };
}

export default useChartGeneration;
